/**
 * 性能预算 — 延迟/内存/CPU阈值 + 自动告警
 */

export interface BudgetThresholds {
  maxLatencyMs?: number
  maxMemoryMB?: number
  maxCpuPercent?: number
  maxErrorRate?: number
}

export interface BudgetViolation {
  metric: string
  value: number
  threshold: number
  timestamp: number
}

export type BudgetAlertHandler = (violation: BudgetViolation) => void

/**
 * 性能预算管理器
 */
export class PerformanceBudget {
  private thresholds: BudgetThresholds
  private violations: BudgetViolation[] = []
  private handlers: BudgetAlertHandler[] = []
  private measurements: { latencies: number[]; errors: number; total: number } = { latencies: [], errors: 0, total: 0 }

  constructor(thresholds: BudgetThresholds) {
    this.thresholds = thresholds
  }

  /** 记录请求 */
  recordRequest(latencyMs: number, success: boolean): void {
    this.measurements.latencies.push(latencyMs)
    this.measurements.total++
    if (!success) this.measurements.errors++
    if (this.measurements.latencies.length > 1000) this.measurements.latencies.shift()
    this.checkLatency(latencyMs)
    this.checkErrorRate()
  }

  /** 检查内存 */
  checkMemory(): boolean {
    if (!this.thresholds.maxMemoryMB) return true
    const usageMB = process.memoryUsage?.().heapUsed / 1024 / 1024 || 0
    if (usageMB > this.thresholds.maxMemoryMB) {
      this.addViolation('memory', usageMB, this.thresholds.maxMemoryMB)
      return false
    }
    return true
  }

  /** 注册告警处理器 */
  onViolation(handler: BudgetAlertHandler): () => void {
    this.handlers.push(handler)
    return () => { const i = this.handlers.indexOf(handler); if (i >= 0) this.handlers.splice(i, 1) }
  }

  /** 获取违规记录 */
  getViolations(limit = 50): BudgetViolation[] {
    return this.violations.slice(-limit)
  }

  /** 获取当前指标 */
  getMetrics(): { avgLatency: number; p95Latency: number; errorRate: number; totalRequests: number } {
    const latencies = this.measurements.latencies
    const avg = latencies.length > 0 ? latencies.reduce((s, v) => s + v, 0) / latencies.length : 0
    const sorted = [...latencies].sort((a, b) => a - b)
    const p95 = sorted.length > 0 ? sorted[Math.floor(sorted.length * 0.95)] : 0
    const errorRate = this.measurements.total > 0 ? this.measurements.errors / this.measurements.total : 0
    return { avgLatency: avg, p95Latency: p95, errorRate, totalRequests: this.measurements.total }
  }

  /** 是否在预算内 */
  isWithinBudget(): boolean {
    const metrics = this.getMetrics()
    if (this.thresholds.maxLatencyMs && metrics.avgLatency > this.thresholds.maxLatencyMs) return false
    if (this.thresholds.maxErrorRate && metrics.errorRate > this.thresholds.maxErrorRate) return false
    return true
  }

  /** 重置 */
  reset(): void {
    this.measurements = { latencies: [], errors: 0, total: 0 }
    this.violations = []
  }

  private checkLatency(latencyMs: number): void {
    if (this.thresholds.maxLatencyMs && latencyMs > this.thresholds.maxLatencyMs) {
      this.addViolation('latency', latencyMs, this.thresholds.maxLatencyMs)
    }
  }

  private checkErrorRate(): void {
    if (!this.thresholds.maxErrorRate || this.measurements.total < 10) return
    const errorRate = this.measurements.errors / this.measurements.total
    if (errorRate > this.thresholds.maxErrorRate) {
      this.addViolation('errorRate', errorRate, this.thresholds.maxErrorRate)
    }
  }

  private addViolation(metric: string, value: number, threshold: number): void {
    const v: BudgetViolation = { metric, value, threshold, timestamp: Date.now() }
    this.violations.push(v)
    if (this.violations.length > 500) this.violations.shift()
    for (const h of this.handlers) { try { h(v) } catch {} }
  }
}
